import { Label } from "components/label";
import { CollapsibleSection } from "../../../../../components/collapsibleSection";
import Input from "components/input";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { HttpError, useUpdate } from "@refinedev/core";
import { IBanner, IHomepageDetailData } from "interfaces";
import { z } from "zod";
import Dropzone from "components/dropzone";
import { zodResolver } from "@hookform/resolvers/zod";
import Button from "components/button";
import { useNavigate } from "react-router-dom";
import { get } from "lodash";
import toast from "react-hot-toast";
import { FullScreenLoading } from "components/fullScreenLoading";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";

interface IBannerField extends IBanner {
  id: string;
}

const EditPromotionBannerSchema = z.object({
  banner: z.string().min(1, "This field is required"),
  mobile_banner: z.string().min(1, "This field is required"),
  open_url: z.string(),
  position: z.union([
    z.string().min(1, "This field is required"),
    z
      .number({
        invalid_type_error: "Position must be a number",
      })
      .min(0, { message: "Position must be equal or greater than 0" }),
  ]),
});

const EditPromotionBannersSchema = z.object({
  data: z.array(EditPromotionBannerSchema),
});

export const PromotionBannerForm = ({
  defaultData,
  setPreviewImageModalData,
}: {
  defaultData: IHomepageDetailData["ads-banner"]["promotion-banner"];
  setPreviewImageModalData: ({
    isOpen,
    image,
  }: {
    isOpen: boolean;
    image: string;
  }) => void;
}) => {
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ data: IBanner[] }, HttpError, { data: IBanner[] }>({
    defaultValues: {
      data: defaultData.data,
    },
    resolver: zodResolver(EditPromotionBannersSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "data",
  });

  const { mutate, isLoading: isLoading } = useUpdate();

  const onSubmit = (values: { data: IBanner[] }) => {
    const submitValues = { ...values };
    mutate(
      {
        resource: `content/homepage`,
        id: `${defaultData.id}/promotion-banner/ads-banner/update`,
        invalidates: [],
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...submitValues,
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          navigate(`/mkt/homepage/detail?default=banner`);
        },
      }
    );
  };

  return (
    <CollapsibleSection label="Promotion Banner" defaultOpen={false}>
      <form
        className="divide-y divide-gray-100"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="w-full px-4 py-6 sm:px-6">
          <div className="w-full">
            <Button
              type="button"
              className="w-fit p-2.5 text-sm bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
              onClick={() => append({ position: 0, banner: "", open_url: "" })}
            >
              <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
              <span className="shrink-0 text-sm">Add more</span>
            </Button>
          </div>
          <div className="flex flex-col gap-y-6 divide-y divide-gray-300">
            {fields.map((field: IBannerField, index: number) => (
              <div
                className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6 py-4"
                key={field.id}
              >
                <div className="col-span-full flex justify-end">
                  <button onClick={() => remove(index)}>
                    <TrashIcon className="w-6 h-6 text-brand-500" />
                  </button>
                </div>
                <div className="col-span-full flex flex-col gap-4 sm:gap-6">
                  <div className="flex-1 grid sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <Label id="position" name="Position" required />
                      <Controller
                        name={`data.${index}.position`}
                        control={control}
                        defaultValue={field.position}
                        render={({ field }) => (
                          <Input
                            id={`position_${index}`}
                            type="number"
                            placeholder="Position"
                            error={get(errors.data, `${index}.position`, "")}
                            min={0}
                            {...field}
                            value={field.value}
                            onChange={(e: any) => field.onChange(e.target.value)}
                          />
                        )}
                      />
                      {get(errors.data, `${index}.position`, "") ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(errors.data, `${index}.position.message`, "")}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <Label id="open_url" name="URL" />
                      <Controller
                        name={`data.${index}.open_url`}
                        control={control}
                        defaultValue={field.open_url ?? ""}
                        render={({ field }) => (
                          <Input
                            id={`open_url_${index}`}
                            type="text"
                            placeholder="URL"
                            error={get(errors.data, `${index}.open_url`, "")}
                            {...field}
                          />
                        )}
                      />
                      {get(errors.data, `${index}.open_url`, "") ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(errors.data, `${index}.open_url.message`, "")}
                          </span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex-1 grid sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <Label id="banner" name="Banner" required />
                      <div>
                        <Controller
                          control={control}
                          name={`data.${index}.banner`}
                          defaultValue={field.banner ?? ""}
                          render={({ field: { onChange, value = "" } }) => {
                            return (
                              <Dropzone
                                id={`data.${index}.banner`}
                                onChange={(imageUrl: string) => onChange(imageUrl)}
                                value={value}
                              />
                            );
                          }}
                        />
                      </div>
                      {get(errors.data, `${index}.banner`, "") ? (
                        <p className="error-text">
                          <span className="font-medium">
                            {get(errors.data, `${index}.banner.message`, "")}
                          </span>
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <Label id="mobile_banner" name="Mobile Banner" required />
                        <div>
                          <Controller
                            control={control}
                            name={`data.${index}.mobile_banner`}
                            defaultValue={field.mobile_banner ?? ""}
                            render={({ field: { onChange, value = "" } }) => {
                            return (
                                <Dropzone
                                  id={`data.${index}.mobile_banner`}
                                  onChange={(imageUrl: string) => onChange(imageUrl)}
                                  value={value}
                                />
                              );
                            }}
                          />
                        </div>
                        {get(errors.data, `${index}.mobile_banner`, "") ? (
                          <p className="error-text">
                            <span className="font-medium">
                              {get(errors.data, `${index}.mobile_banner.message`, "")}
                            </span>
                          </p>
                        ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="w-full px-4 py-4 sm:px-6 bg-white">
          <div className="flex items-center justify-end gap-x-4">
            <Button className="w-20" type="submit">
              SAVE
            </Button>
          </div>
        </div>
      </form>
      {isLoading ? <FullScreenLoading /> : <></>}
    </CollapsibleSection>
  );
};
